import styled from "styled-components";
import { SP_BREAKPOINT } from "../../constants/app";
import {
  TableHeaderItem,
  TablePagination,
  TableSortBy,
  useTable,
} from "../../hooks/useTable";
import { Button, ButtonVariant } from "../atoms/Button";
import { IconComponent } from "../atoms/icons/types";
import { Pagination } from "../organisms/Pagination";
import { Table } from "../organisms/table/";
import { FileTypeNumber } from "../../core/api/requests/file/download/types";

type Props<DataType> = {
  title?: string;
  number?: string;
  button?: {
    text: string;
    variant: ButtonVariant;
    onClick?: () => void;
    icon?: IconComponent;
  };
  headers: TableHeaderItem<DataType>[];
  data: DataType[];
  defaultSort?: TableSortBy;
  selectable?: boolean;
  editable?: boolean;
  deletable?: boolean;
  downloadable?: boolean;
  enableCSVDownload?: boolean;
  hasPagination?: boolean;
  perPage?: number;
  total?: number;
  stickyHeader?: boolean;
  defaultEmptyText?: string;
  enableHoverPopup?: boolean;
  fetchDataFunction: (sort: TableSortBy, pagination?: TablePagination) => void;
  onEdit?: (data: DataType) => void;
  onBulkDelete?: () => void;
  onDownload?: (data: DataType, fileType: FileTypeNumber) => void;
  onCSVDownload?: (data: DataType, fileType: FileTypeNumber) => void;
  onDelete?: (data: DataType) => void;
  onSelect?: (data: DataType, checked: boolean) => void;
  onSelectAll?: (checked: boolean) => void;
};

export const TableTemplate = <DataType extends {}>({
  title,
  number,
  button,
  headers,
  data,
  selectable,
  defaultSort,
  editable,
  deletable,
  downloadable,
  enableCSVDownload,
  hasPagination,
  perPage,
  total,
  stickyHeader,
  defaultEmptyText,
  enableHoverPopup,
  fetchDataFunction,
  onEdit,
  onDownload,
  onCSVDownload,
  onDelete,
  onBulkDelete,
  onSelect,
  onSelectAll,
}: Props<DataType>) => {
  const {
    headersToShow,
    dataToShow,
    currentPage,
    totalPage,
    setCurrentPage,
    onNextPage,
    onPreviousPage,
    disableBulkDeleteButton,
  } = useTable<DataType>({
    headers,
    data,
    fetchDataFunction,
    defaultSort,
    perPage,
    total: total ?? data.length,
  });

  return (
    <StyledContainer>
      <StyledTableTemplateContainer $stickyHeader={stickyHeader}>
        {title && (
          <StyledHeader>
            <StyleTitleContainer>
              <StyledHeaderTitle>{title}</StyledHeaderTitle>
              {number && <StyledNumber>{number}</StyledNumber>}
            </StyleTitleContainer>

            {button && <Button {...button} />}
          </StyledHeader>
        )}

        <StyledTableContainer $stickyHeader={stickyHeader}>
          <Table<DataType>
            headers={headersToShow}
            data={dataToShow}
            selectable={selectable}
            editable={editable}
            deletable={deletable}
            downloadable={downloadable}
            enableCSVDownload={enableCSVDownload}
            stickyHeader={stickyHeader}
            defaultEmptyText={defaultEmptyText}
            disableDeleteButton={disableBulkDeleteButton}
            enableHoverPopup={enableHoverPopup}
            onEdit={onEdit}
            onBulkDelete={onBulkDelete}
            onDelete={onDelete}
            onDownload={onDownload}
            onCSVDownload={onCSVDownload}
            onSelect={onSelect}
            onSelectAll={onSelectAll}
          />
        </StyledTableContainer>

        {hasPagination && (
          <Pagination
            page={currentPage}
            totalPage={totalPage}
            onSetPage={setCurrentPage}
            onPrevious={onPreviousPage}
            onNext={onNextPage}
          />
        )}
      </StyledTableTemplateContainer>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const StyledTableTemplateContainer = styled.div<{ $stickyHeader?: boolean }>`
  @media (max-width: ${SP_BREAKPOINT}) {
    ${({ $stickyHeader }) => ($stickyHeader ? `padding-bottom: 16px;` : ``)}
  }
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow: hidden;
  background-color: #ffffff;
  ${({ $stickyHeader }) => ($stickyHeader ? `height: 100%; ` : ``)}
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const StyledHeaderTitle = styled.div`
  @media (max-width: ${SP_BREAKPOINT}) {
    font-size: 14px;
  }
  font-size: 20px;
  font-weight: 400;
  line-height: 100%;
`;

const StyledTableContainer = styled.div<{ $stickyHeader?: boolean }>`
  ${({ $stickyHeader }) =>
    $stickyHeader ? `position: relative; height: 100%;` : ``}

  max-width: 100%;
  max-height: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StyleTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledNumber = styled.div`
  @media (max-width: ${SP_BREAKPOINT}) {
    font-size: 13px;
  }
  font-size: 20px;
`;
